// Competência (mês de referência). Protótipo: getMonth (1120), compKey (2505), listaCompetencias,
// seletor de mês/ano do vistoriador (1364) e gestCompOptions (4083).

import type { Competencia, DataCivil } from './tipos.js';

export const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
] as const;

/** "Março/2026" */
export function rotuloCompetencia(c: Competencia): string {
  return `${MESES[c.mes - 1]}/${c.ano}`;
}

/** Aceita "Março/2026". Devolve null se não reconhecer. */
export function lerRotuloCompetencia(rotulo: string): Competencia | null {
  const [m, a] = String(rotulo ?? '').split('/');
  const mi = MESES.indexOf(m as (typeof MESES)[number]);
  const ano = Number.parseInt(a ?? '', 10);
  if (mi < 0 || !Number.isFinite(ano)) return null;
  return { ano, mes: mi + 1 };
}

/** "2026-03-01" (formato da coluna date no banco). */
export function competenciaParaIso(c: Competencia): string {
  return `${c.ano}-${String(c.mes).padStart(2, '0')}-01`;
}

export function competenciaDeIso(iso: string): Competencia {
  const [a, m] = iso.slice(0, 10).split('-');
  return { ano: Number(a), mes: Number(m) };
}

export function chaveCompetencia(c: Competencia): number {
  return c.ano * 100 + c.mes;
}

export function mesmaCompetencia(a: Competencia, b: Competencia): boolean {
  return a.ano === b.ano && a.mes === b.mes;
}

export function competenciaDe(d: DataCivil): Competencia {
  return { ano: d.ano, mes: d.mes };
}

function unicasOrdenadasDesc(lista: Competencia[]): Competencia[] {
  const mapa = new Map<number, Competencia>();
  for (const c of lista) mapa.set(chaveCompetencia(c), { ano: c.ano, mes: c.mes });
  return [...mapa.values()].sort((a, b) => chaveCompetencia(b) - chaveCompetencia(a));
}

/**
 * Competências oferecidas nos seletores da gestão: as que têm vistoria + o mês atual
 * (+ a que está sendo vista), da mais recente para a mais antiga.
 */
export function listaCompetencias(usadas: Competencia[], hoje: DataCivil, vista?: Competencia | null): Competencia[] {
  const todas = [...usadas, competenciaDe(hoje)];
  if (vista) todas.push(vista);
  return unicasOrdenadasDesc(todas);
}

/** Anos do seletor do vistoriador: de 2 anos atrás até o próximo, mais o selecionado; decrescente. */
export function anosDoVistoriador(hoje: DataCivil, anoSelecionado: number): number[] {
  const anos: number[] = [];
  for (let y = hoje.ano - 2; y <= hoje.ano + 1; y++) anos.push(y);
  if (!anos.includes(anoSelecionado)) anos.push(anoSelecionado);
  return anos.sort((a, b) => b - a);
}

/**
 * Competências do formulário de gasto: os últimos 18 meses, os 12 do ano filtrado e as já
 * usadas em lançamentos; decrescente.
 */
export function competenciasDeGasto(hoje: DataCivil, anoFiltro: number, usadas: Competencia[]): Competencia[] {
  const lista: Competencia[] = [];
  for (let i = 0; i < 18; i++) {
    const idx = hoje.ano * 12 + (hoje.mes - 1) - i;
    lista.push({ ano: Math.floor(idx / 12), mes: (idx % 12) + 1 });
  }
  for (let m = 1; m <= 12; m++) lista.push({ ano: anoFiltro, mes: m });
  return unicasOrdenadasDesc([...lista, ...usadas]);
}
